import {ArrayUtils, DateUtils, NoDataMessageWithIcon} from '@wallaby-ui/core';
import {
  CHART_MONTH_TICKFORMAT,
  ChartColor,
  Formatters,
  getLineChartItems,
  LightChart,
  LightChartLayout,
  LightLineChartTrace,
} from '@wallaby-ui/document';
import {parseISO} from 'date-fns';
import * as React from 'react';
import {useMemo} from 'react';

import {DashboardDataType} from './HaidiDashboardLayoutContainer';

type Props = {
  confirmedHais: DashboardDataType['confirmedHaisByMonth'] | undefined;
  colonizations: DashboardDataType['colonizedHaisByMonth'] | undefined;
  chartColors: ChartColor[];
  formatters: Formatters;
};
export const DashboardMonthChart: React.FC<Props> = (props) => {
  const {confirmedHais, colonizations, chartColors, formatters} = props;
  const {t} = formatters;
  const parseChartData = (v: DashboardDataType['colonizedHaisByMonth'][0]) => ({
    timestamp: DateUtils.fromReverseMonthFormat(v.month ?? '').toISOString(),
    count: v.count ?? 0,
    state: v.state,
  });
  // Month buckets may arrive out of chronological order; sort ascending so the line doesn't jump backwards.
  const byTimestampAsc = (a: {timestamp: string}, b: {timestamp: string}) =>
    new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
  const unboundChartData = ArrayUtils.zip(
    (confirmedHais ?? []).map(parseChartData).sort(byTimestampAsc),
    (colonizations ?? []).map(parseChartData).sort(byTimestampAsc)
  );
  const chartData: LightLineChartTrace[] = useMemo(() => {
    const {formatFloat} = formatters;
    return getLineChartItems(unboundChartData, chartColors, (v, i) => ({
      x: v[i]?.timestamp ?? '',
      y: v[i]?.count ?? 0,
      tooltip: `${DateUtils.toReverseMonthFormat(parseISO(v[i]?.timestamp ?? ''))}<br><b>${chartColors[i].label}</b>: ${formatFloat(
        v[i]?.count ?? 0
      )}`,
    })).map((v) => ({...v, cliponaxis: false}));
  }, [unboundChartData, chartColors, formatters]);

  const layout: LightChartLayout = {
    xaxis: {
      type: 'date',
      tickformat: CHART_MONTH_TICKFORMAT,
      automargin: true,
    },
    yaxis: {
      automargin: true,
      tickmode: 'auto',
      nticks: 6,
    },
    showlegend: false,
  };
  const noDataComponent = useMemo(() => <NoDataMessageWithIcon message={'chart.emptyData'} isHaidi />, []);
  return (
    <LightChart
      data={chartData}
      layout={layout}
      imageDownload={{
        title: {
          text: t('dashboard.confirmedHaisAndColonizations'),
        },
        showlegend: true,
      }}
      noDataComponent={noDataComponent}
    />
  );
};
